import React, { useState, useEffect } from 'react';
import { 
  X, 
  User, 
  Calendar,
  Clock,
  CreditCard,
  DollarSign,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  FileText
} from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from './LoadingSpinner';

const PaymentHistoryModal = ({ 
  isOpen, 
  onClose, 
  eleve,
  moisId,
  moisNom,
  anneeActive,
  formatMontant,
  categorie = 'scolarite'
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [paiementActuel, setPaiementActuel] = useState(null);
  
  useEffect(() => {
    if (isOpen && eleve && moisId && anneeActive) {
      fetchHistorique();
    }
  }, [isOpen, eleve, moisId, anneeActive, categorie]);
  
  const fetchHistorique = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await api.finance.getHistoriquePaiementDetail(
        eleve.id, 
        moisId, 
        anneeActive.id, 
        categorie || 'scolarite'
      );
      
      if (response.success && response.data) {
        setHistorique(response.data.historique || []);
        setPaiementActuel(response.data.paiement_actuel);
      } else {
        setError("Impossible de récupérer l'historique des paiements");
      }
    } catch (error) {
      console.error("Erreur historique:", error);
      setError(error.message || "Une erreur est survenue");
    } finally {
      setIsLoading(false);
    }
  };
  
  const getModeIcon = (mode) => {
    const icons = {
      'Espèces': <DollarSign size={16} className="text-green-600" />,
      'Chèque': <FileText size={16} className="text-blue-600" />,
      'Virement': <CreditCard size={16} className="text-purple-600" />,
      'Transfert Mobile': <CreditCard size={16} className="text-orange-600" />
    };
    return icons[mode] || <CreditCard size={16} className="text-gray-600" />;
  };

  if (!isOpen || !eleve) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
    >
      <div 
        className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="bg-[#246990] px-5 py-3 rounded-t-lg flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-white">
              Historique des paiements - {moisNom}
            </h2>
            <p className="text-white/80 text-sm mt-0.5">
              {eleve.prenom} {eleve.nom} - {eleve.classe_nom}
            </p>
          </div>
          <div 
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-1 transition-colors cursor-pointer"
          >
            <X size={20} />
          </div>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto flex-1">
          {/* Statut actuel */}
          {paiementActuel && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle size={16} className="text-[#246990]" />
                <h3 className="font-semibold text-gray-900 text-sm">Statut actuel</h3>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-gray-50 rounded border border-gray-200">
                  <p className="text-lg font-bold text-gray-900">{formatMontant(paiementActuel.montant_attendu || 0)}</p>
                  <p className="text-xs text-gray-500 mt-1">Montant dû</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded border border-green-200">
                  <p className="text-lg font-bold text-green-600">{formatMontant(paiementActuel.montant_paye || 0)}</p>
                  <p className="text-xs text-green-700 mt-1">Montant payé</p>
                </div>
                <div className={`text-center p-3 rounded border ${
                  paiementActuel.statut === 'Payé' 
                    ? 'bg-blue-50 border-blue-200' 
                    : paiementActuel.statut === 'Partiel'
                      ? 'bg-yellow-50 border-yellow-200'
                      : 'bg-red-50 border-red-200'
                }`}>
                  <p className={`text-lg font-bold ${
                    paiementActuel.statut === 'Payé' 
                      ? 'text-blue-600' 
                      : paiementActuel.statut === 'Partiel'
                        ? 'text-yellow-600'
                        : 'text-red-600'
                  }`}>
                    {paiementActuel.statut}
                  </p>
                  <p className={`text-xs mt-1 ${
                    paiementActuel.statut === 'Payé' 
                      ? 'text-blue-700' 
                      : paiementActuel.statut === 'Partiel'
                        ? 'text-yellow-700'
                        : 'text-red-700'
                  }`}>
                    Statut
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Historique */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FileText size={16} className="text-[#246990]" />
              <h3 className="font-semibold text-gray-900 text-sm">Historique des transactions</h3>
            </div>

            {isLoading ? (
              <LoadingSpinner message="Chargement de l'historique..." size="sm" />
            ) : error ? (
              <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded">
                <AlertCircle className="text-red-600 flex-shrink-0" size={18} />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            ) : historique.length === 0 ? (
              <div className="text-center p-8 bg-gray-50 rounded-lg border border-gray-200">
                <FileText size={32} className="mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-500">Aucune transaction enregistrée</p>
              </div>
            ) : (
              <div className="space-y-3">
                {historique.map((transaction, index) => (
                  <div 
                    key={transaction.id || index} 
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        {getModeIcon(transaction.mode_paiement)}
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">
                            {transaction.mode_paiement || "Paiement"}
                          </p>
                          {transaction.numero_transaction && (
                            <p className="text-xs text-gray-500">
                              Réf: {transaction.numero_transaction}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-[#246990]">
                          {formatMontant(transaction.montant || 0)}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-xs border-t border-gray-100 pt-3">
                      <div className="flex items-start gap-2">
                        <Calendar size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-gray-500 mb-0.5">Date</p>
                          <p className="font-medium text-gray-900">
                            {transaction.date_formatee || new Date(transaction.date_paiement).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>

                      {transaction.heure && (
                        <div className="flex items-start gap-2">
                          <Clock size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-gray-500 mb-0.5">Heure</p>
                            <p className="font-medium text-gray-900">{transaction.heure}</p>
                          </div>
                        </div>
                      )}

                      {(transaction.agent_nom || transaction.employe_nom) && (
                        <div className="flex items-start gap-2">
                          <User size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-gray-500 mb-0.5">Enregistré par</p>
                            <p className="font-medium text-gray-900">
                              {transaction.agent_prenom} {transaction.agent_nom || transaction.employe_nom}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {transaction.commentaire && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-start gap-2">
                          <MessageSquare size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-gray-500 text-xs mb-0.5">Commentaire</p>
                            <p className="text-gray-700 text-sm italic">{transaction.commentaire}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-5 py-3 rounded-b-lg border-t border-gray-200 flex justify-between items-center flex-shrink-0">
          <p className="text-xs text-gray-600">
            Historique de <strong className="text-gray-900">{eleve.prenom} {eleve.nom}</strong> pour {moisNom}
          </p>
          <div
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
          >
            Fermer
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentHistoryModal;